import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { fieldSizes, fieldStyles, type FieldSize } from "./fieldStyles";
import { useFieldControlProps } from "./FormGroup";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  /** Shorthand for <option> children. */
  options?: readonly SelectOption[];
  /** Disabled first option shown while nothing is selected. */
  placeholder?: string;
  size?: FieldSize;
  invalid?: boolean;
}

/** Native select (keyboard and mobile pickers for free) in the field style. */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, size = "md", invalid, className, children, ...props }, ref) => {
    const controlProps = useFieldControlProps(props, invalid);
    const uncontrolledDefault =
      placeholder !== undefined && props.value === undefined && props.defaultValue === undefined
        ? ""
        : undefined;
    return (
      <div className="relative">
        <select
          ref={ref}
          defaultValue={uncontrolledDefault}
          className={cn(
            fieldStyles,
            fieldSizes[size],
            "cursor-pointer appearance-none pr-10",
            className,
          )}
          {...controlProps}
        >
          {placeholder !== undefined && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options?.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-gold-primary"
        />
      </div>
    );
  },
);
Select.displayName = "Select";
